import React, { useCallback, useRef, useState } from 'react'

import type { AdapterKitControlProps } from 'components/formKit/types'
import { IconButton } from 'components/uiKit/Button'
import Dropdown from 'components/uiKit/Dropdown'
import type { IInputProps } from 'components/uiKit/Input'
import Input, { AdditionWrapper } from 'components/uiKit/Input'
import { KitSize } from 'components/uiKit/KitTypes'
import { isMixed } from 'components/uiKit/KitUtils'

import PasswordTips from './PasswordTips'

export interface IPasswordProps extends IInputProps {
  showTips?: boolean
}

const FieldPassword = ({
  name,
  onChange,
  showTips = false,
  ...rest
}: AdapterKitControlProps<IPasswordProps, string>) => {
  const ref = useRef<HTMLInputElement>(null)
  const [hidden, setHidden] = useState(true)
  const [showTipsDropdown, setShowTipsDropdown] = useState(false)

  const handleFocus = useCallback(() => {
    if (showTips) {
      setShowTipsDropdown(true)
    }
  }, [showTips])

  const handleBlur = useCallback(() => {
    if (showTips) {
      setShowTipsDropdown(false)
    }
  }, [showTips])

  const onHiddenClick = useCallback(() => {
    setHidden((v) => !v)
    ref.current?.focus()
  }, [ref])

  const handleChange = useCallback((value: string) => onChange({ name, value }), [name, onChange])

  return (
    <Dropdown
      closeOnRootClick={false}
      disabled={!showTips}
      onBlur={handleBlur}
      onFocus={handleFocus}
      onVisibleChange={setShowTipsDropdown}
      overlay={<PasswordTips value={isMixed(rest.value) ? undefined : rest.value} />}
      placement='right'
      visible={showTipsDropdown}
      fluid
    >
      <Input
        {...rest}
        name={name}
        onChange={handleChange}
        postfix={
          <AdditionWrapper>
            <IconButton
              icon={hidden ? 'eyeCloseEye' : 'eyeOpenEye'}
              name={`${name}.togglePassword`}
              onClick={onHiddenClick}
              onMouseDown={(e) => e.preventDefault()}
              onMouseUp={(e) => e.preventDefault()}
              size={KitSize.S}
              styleType='ghost'
              type='button'
            />
          </AdditionWrapper>
        }
        ref={ref}
        security=''
        type={hidden ? 'password' : 'text'}
      />
    </Dropdown>
  )
}

export default React.memo(FieldPassword) as typeof FieldPassword
